'use client';

import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import clsx from 'clsx';
import { useMemo, useState } from 'react';

import CustomLink from '../../ui/custom-link';
import { ImageSlider } from '../../ui/image-slider';
import { RankingStatusBadgeEnum } from '../RankingStatusBadge/types';
import { TOURNAMENT_PLACEHOLDER_IMAGE } from '../utils/constants';

export function renderPlayerTourneyCardLabels(tournament: TournamentCardProps): string {
	if (tournament.isCanceled) {
		return 'canceled';
	}

	const tourneyEndDate = new Date(tournament.dateTo);
	const now = new Date();

	if (tourneyEndDate < now) {
		return 'completed';
	}

	if (tournament.isRegistrationClosed) {
		return 'registration-closed';
	}

	if (tournament.price > 0) {
		return 'price';
	}

	if (tournament.isCostPerEvent) {
		return 'cost-per-event';
	}

	return 'free';
}

export interface TournamentCardProps {
	id: string;
	dateFrom: string;
	dateTo: string;
	isCanceled?: boolean;
	isCostPerEvent?: boolean;
	currency: string;
	location: string;
	isRegistrationClosed: boolean;
	registrationDateClosed?: Date | undefined;
	logo: string;
	price: number;
	registrationCount: number;
	slug: string;
	status?: string;
	title: string;
	isTournamentsApp?: boolean;
	tournamentsAppBaseURL: string;
	additionalImages?: string[];
	customCardHref?: string;
	isAdvertiseOnly?: boolean;
	hideRegisteredPlayers?: boolean;
	className?: string;
	logoClassName?: string;
	isFavorite?: boolean;
	badgeTitle?: RankingStatusBadgeEnum;
	showBadgeTitle?: boolean;
	hideCardHref?: boolean;
	/**
	 * Hide the entire status badge block (`Playing Now` / `Completed` / `Reg. Closed` / price / `Cost Per Event` /
	 * `Free`). Used by the listing-ad rotator on the search page, where the card is decorative — the badge
	 * computed from placeholder price / status values would otherwise mislead viewers about the underlying
	 * tournament's actual state. Mirrors `LeagueCard`'s identically-named prop.
	 */
	shouldHideBadge?: boolean;
	/**
	 * When paired with `customCardHref`, opens the click-through in a new tab (`target="_blank"` + safe
	 * `rel="noopener noreferrer"`). No effect when `customCardHref` is not set. Mirrors `LeagueCard`'s
	 * identically-named prop.
	 */
	openInNewTab?: boolean;
	/**
	 * Render the click-through as a plain `<a>` (full document navigation) instead of a Next `<Link>`.
	 * Set by the listing-ad rotator: an ad's URL carries Everflow parameters that the landing middleware
	 * has to see, and Next would route a same-origin URL client-side and skip it.
	 */
	nativeAnchor?: boolean;
	/**
	 * Suppresses the entire bottom row (status badge + registration count) — useful when the surrounding
	 * surface (e.g. listing-ad rotator on the clubs search page) wants the card to collapse to title +
	 * location + dates only, so it sizes to match neighbouring `MyClubCard`s.
	 */
	hideBottomRow?: boolean;
}

export const TournamentCard = (props: TournamentCardProps) => {
	const { additionalImages, id, slug, customCardHref, openInNewTab, nativeAnchor, tournamentsAppBaseURL, status, hideCardHref = false } = props;

	const newStatus = status ? status : renderPlayerTourneyCardLabels(props);

	// `h-full w-full` on the link is required so the inner card's `!size-full` resolves against a parent
	// with a defined height — the listing-ad rotator wraps the card in a grid item with `h-full`, and
	// without filling the link the card collapses to content height and breaks alignment with neighbors.
	// Safe in non-ad contexts too: block elements with `h-full` inside grid cells just stretch to the cell.
	return (additionalImages && additionalImages.length > 0) || hideCardHref ? (
		<TournamentCardComponent {...props} status={newStatus} />
	) : customCardHref ? (
		<CustomLink
			href={customCardHref}
			className="block h-full w-full"
			target={openInNewTab ? '_blank' : undefined}
			rel={openInNewTab ? 'noopener noreferrer' : undefined}
			native={nativeAnchor}
		>
			<TournamentCardComponent {...props} status={newStatus} />
		</CustomLink>
	) : (
		<CustomLink
			href={`${tournamentsAppBaseURL}/tournaments/${slug ? slug : id}${newStatus === 'completed' ? '/winners-circle' : ''}`}
			className="block h-full w-full"
			native={nativeAnchor}
		>
			<TournamentCardComponent {...props} status={newStatus} />
		</CustomLink>
	);
};

export const TournamentCardComponent = ({
	id,
	dateFrom,
	dateTo,
	logo,
	currency,
	location,
	price,
	slug,
	registrationCount,
	registrationDateClosed,
	status,
	title,
	additionalImages,
	hideRegisteredPlayers,
	isAdvertiseOnly,
	className,
	logoClassName,
	tournamentsAppBaseURL,
	shouldHideBadge,
	hideBottomRow
}: TournamentCardProps) => {
	const tourneyFromDate = new Date(dateFrom);
	const tourneyToDate = new Date(dateTo);
	const currentDate = new Date();

	// Using parseZone() to show dates in Tourney's timezone
	const formattedFromDate = formatDateTime(dateFrom, { parseZone: true });
	const formattedToDate = formatDateTime(dateTo, { parseZone: true });

	const isPlayingNow = currentDate >= tourneyFromDate && currentDate <= tourneyToDate && status !== 'completed';

	const [isPlaceholderImage, setIsPlaceholderImage] = useState(false);

	const isLessThan48Hours =
		registrationDateClosed && registrationDateClosed instanceof Date
			? registrationDateClosed.getTime() - currentDate.getTime() >= 0 &&
				registrationDateClosed.getTime() - currentDate.getTime() <= 48 * 60 * 60 * 1000
			: false;
	const getImageToDisplay = useMemo(() => {
		if (logo && (logo.toLowerCase().includes('/uploads/clubs/') || logo.toLowerCase().includes('/uploads/events/'))) {
			return getCDNURL(logo, 566, 384);
		}
		setIsPlaceholderImage(true);
		return getCDNURL(TOURNAMENT_PLACEHOLDER_IMAGE, 566, 384);
	}, [logo]);

	return (
		<div
			className={clsx(
				className,
				'group relative flex cursor-pointer !size-full min-[500px]:min-w-[224px] min-[500px]:max-w-[286px] flex-col rounded-xl border bg-white'
			)}
		>
			{isLessThan48Hours && status !== 'closed' ? (
				<div className="absolute top-2 left-2 z-30 px-2 py-1 text-sm rounded-md bg-success-700 text-white">Closing Soon</div>
			) : null}

			<div
				className={clsx(
					logoClassName,
					'relative flex items-center w-full justify-center overflow-hidden rounded-t-xl border-b border-b-gray-200 bg-white'
				)}
				style={{ aspectRatio: '16 / 9' }}
			>
				{additionalImages && additionalImages.length > 0 ? (
					<ImageSlider
						customHref={`/tournaments/${slug}`}
						size="sm"
						containerClassNames="h-full"
						images={[getImageToDisplay, ...additionalImages]}
						objectCover
						isPlaceholderImage={isPlaceholderImage}
					/>
				) : (
					<img
						src={getImageToDisplay}
						alt={title}
						className={clsx(logoClassName, 'block h-full rounded-t-xl object-cover', isPlaceholderImage && 'py-4')}
					/>
				)}
			</div>
			{additionalImages && additionalImages.length > 0 ? (
				<CustomLink
					href={`${tournamentsAppBaseURL}/tournaments/${slug ? slug : id}${status === 'completed' ? '/winners-circle' : ''}`}
					className="block"
				>
					<div className="flex flex-1 flex-col rounded-b-xl justify-between gap-2 p-4 pt-3 relative z-20 group-hover:bg-gray-50">
						{/* See sibling render branch below for why the top items are grouped. */}
						<div className="flex flex-col gap-2">
							<div className="!line-clamp-2 block text-sm font-medium text-gray-900" title={title} style={{ minHeight: '2.5rem' }}>
								{title}
							</div>

							{location ? (
								<div className="line-clamp-1 text-sm text-gray-600" style={{ minHeight: '1rem' }}>
									{location}
								</div>
							) : null}
							{dateFrom || dateTo ? (
								<div className="text-sm line-clamp-1 text-gray-700" style={{ minHeight: '1.25rem' }}>
									{`${formattedFromDate} - ${formattedToDate}`}
								</div>
							) : null}
						</div>
						{!hideBottomRow && (status || registrationCount) ? (
							<div className="mt-2 flex flex-row flex-wrap items-center justify-between gap-1">
								{!shouldHideBadge && (
									<>
										{isPlayingNow ? (
											<TournamentCardBadge type="default">
												<p className="text-sm font-semibold">Playing Now</p>
											</TournamentCardBadge>
										) : null}
										{status === 'completed' && (
											<TournamentCardBadge type="closed">
												<p className="text-sm font-semibold">Completed</p>
											</TournamentCardBadge>
										)}
										{status === 'registration-closed' && !isPlayingNow && (
											<TournamentCardBadge type="closed">
												<p className="text-sm font-semibold">Reg. Closed</p>
											</TournamentCardBadge>
										)}
										{status === 'price' && !isPlayingNow && (
											<TournamentCardBadge type="default">
												<p className="text-sm font-semibold">{`${currency ? currency.toUpperCase() : ''} ${new Intl.NumberFormat('en-US', { style: 'currency', currency: currency, currencyDisplay: 'narrowSymbol' }).format(price)}`}</p>
											</TournamentCardBadge>
										)}
										{status === 'cost-per-event' && !isPlayingNow && (
											<TournamentCardBadge type="default">
												<p className="text-sm font-semibold">Cost Per Event</p>
											</TournamentCardBadge>
										)}
										{status === 'free' && !isPlayingNow && (
											<TournamentCardBadge type="default">
												<p className="text-sm font-semibold">Free</p>
											</TournamentCardBadge>
										)}
									</>
								)}
								{!hideRegisteredPlayers &&
									(isAdvertiseOnly ? (
										<div className="text-xs flex text-gray-600 items-center" style={{ minHeight: '1.75rem' }}>
											Advertised
										</div>
									) : (
										<div
											className="text-xs flex text-gray-600 items-center"
											style={{ minHeight: '1.75rem' }}
										>{`${registrationCount} player${registrationCount != 1 ? 's' : ''}`}</div>
									))}
							</div>
						) : null}
					</div>
				</CustomLink>
			) : (
				<div className="flex flex-1 flex-col rounded-b-xl justify-between gap-2 p-4 pt-3 relative z-20 group-hover:bg-gray-50">
					{/* Top group: title + location + dates. Wrapped so the outer `justify-between` only sees
						two children (this group + the bottom badge row). When `hideBottomRow` removes the
						footer, this group is the only child and sits at flex-start naturally — no spreading. */}
					<div className="flex flex-col gap-2">
						<div className="!line-clamp-2 block text-sm font-medium text-gray-900" title={title} style={{ minHeight: '2.5rem' }}>
							{title}
						</div>

						{location ? (
							<div className="line-clamp-1 text-sm text-gray-600" style={{ minHeight: '1.25rem' }}>
								{location}
							</div>
						) : null}
						{dateFrom || dateTo ? (
							<div className="text-sm line-clamp-1 text-gray-700" style={{ minHeight: '1.25rem' }}>
								{`${formattedFromDate} - ${formattedToDate}`}
							</div>
						) : null}
					</div>
					{!hideBottomRow && (status || registrationCount) ? (
						<div className="mt-2 flex flex-row flex-wrap items-center justify-between gap-1">
							{!shouldHideBadge && (
								<>
									{isPlayingNow ? (
										<TournamentCardBadge type="default">
											<p className="text-sm font-semibold">Playing Now</p>
										</TournamentCardBadge>
									) : null}
									{status === 'completed' && (
										<TournamentCardBadge type="closed">
											<p className="text-sm font-semibold">Completed</p>
										</TournamentCardBadge>
									)}
									{status === 'registration-closed' && !isPlayingNow && (
										<TournamentCardBadge type="closed">
											<p className="text-sm font-semibold">Reg. Closed</p>
										</TournamentCardBadge>
									)}
									{status === 'price' && !isPlayingNow && (
										<TournamentCardBadge type="default">
											<p className="text-sm font-semibold">{`${currency ? currency.toUpperCase() : ''} ${new Intl.NumberFormat('en-US', { style: 'currency', currency: currency, currencyDisplay: 'narrowSymbol' }).format(price)}`}</p>
										</TournamentCardBadge>
									)}
									{status === 'cost-per-event' && !isPlayingNow && (
										<TournamentCardBadge type="default">
											<p className="text-sm font-semibold">Cost Per Event</p>
										</TournamentCardBadge>
									)}
									{status === 'free' && !isPlayingNow && (
										<TournamentCardBadge type="default">
											<p className="text-sm font-semibold">Free</p>
										</TournamentCardBadge>
									)}
								</>
							)}
							{!hideRegisteredPlayers &&
								(isAdvertiseOnly ? (
									<div className="text-xs flex text-gray-600 items-center" style={{ minHeight: '1.75rem' }}>
										Advertised
									</div>
								) : (
									<div
										className="text-xs flex text-gray-600 items-center"
										style={{ minHeight: '1.75rem' }}
									>{`${registrationCount} player${registrationCount != 1 ? 's' : ''}`}</div>
								))}
						</div>
					) : null}
				</div>
			)}
		</div>
	);
};

export type TournamentCardBadgeType = 'default' | 'closed';

export const TournamentCardBadge = ({
	children,
	className,
	type
}: {
	children: React.ReactNode;
	className?: string;
	type: TournamentCardBadgeType;
	size?: 'sm' | 'md';
}) => {
	const classnames = clsx(
		'px-2 py-1 text-center text-sm font-semibold rounded-md',
		{
			'bg-success-700 text-white': type === 'default',
			'bg-gray-700 text-white': type === 'closed'
		},
		className
	);

	return <div className={classnames}>{children}</div>;
};
